// PR-MAY14-TRIP-CALENDAR — month-grid view for the trips list.
//
// Toggled from TripsPage. Renders a 7-column grid of the visible
// month with one cell per day. Each cell shows:
//   * the day number
//   * a count chip for trips on that day
//   * up to 3 row-stubs (code + client) — overflow becomes a "+N more"
//
// Clicking the day opens a date-filtered overlay (delegated to the
// parent via onPickDay). Clicking a single trip stub opens that
// trip's detail drawer (delegated via onPickTrip).
//
// Keeps the component free of business state: parent owns trips +
// callbacks.

import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_HEADERS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export interface CalendarTrip {
  id: string;
  code: string;
  tripDate: string;          // YYYY-MM-DD or ISO; we read getFullYear/Month/Date locally
  client?: { nameEn?: string | null } | null;
  status: string;
}

export interface TripCalendarProps {
  trips: CalendarTrip[];
  month: Date;                                      // any Date inside the displayed month
  onMonthChange: (next: Date) => void;
  onPickDay?: (ymd: string) => void;                // YYYY-MM-DD
  onPickTrip?: (id: string) => void;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function TripCalendar({ trips, month, onMonthChange, onPickDay, onPickTrip }: TripCalendarProps) {
  const { weeks, byDay } = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    // Sunday-first grid. Backfill leading + trailing days from neighbour months
    // so the grid is always a clean 7×N.
    const lead = first.getDay();
    const start = new Date(first);
    start.setDate(start.getDate() - lead);

    const cells: Date[] = [];
    const cur = new Date(start);
    for (let i = 0; i < 42; i++) {
      cells.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    // Drop the trailing week if it's entirely in next month (i.e. the
    // current month ends in week 4 or 5).
    const w: Date[][] = [];
    for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
    while (w.length > 0 && w[w.length - 1].every((d) => d.getMonth() !== month.getMonth())) w.pop();

    // Bucket trips by their YYYY-MM-DD.
    const bucket = new Map<string, CalendarTrip[]>();
    for (const t of trips) {
      const date = new Date(t.tripDate);
      if (Number.isNaN(date.getTime())) continue;
      const key = ymd(date);
      if (!bucket.has(key)) bucket.set(key, []);
      bucket.get(key)!.push(t);
    }
    return { weeks: w, byDay: bucket };
  }, [trips, month]);

  const todayKey = ymd(new Date());

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <Button variant="ghost" size="sm" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-sm font-medium">
          {MONTH_NAMES[month.getMonth()]} {month.getFullYear()}
        </div>
        <Button variant="ghost" size="sm" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/10 border-b border-border">
        {DAY_HEADERS.map((d) => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 auto-rows-[110px]">
        {weeks.flat().map((d, i) => {
          const key = ymd(d);
          const inMonth = d.getMonth() === month.getMonth();
          const dayTrips = byDay.get(key) ?? [];
          const isToday = key === todayKey;
          return (
            <div
              key={i}
              className={[
                'p-1.5 border-b border-r border-border last:border-r-0 cursor-pointer transition-colors',
                inMonth ? 'bg-card hover:bg-muted/20' : 'bg-muted/5 text-muted-foreground/60',
                isToday ? 'ring-1 ring-inset ring-blue-500/40' : '',
              ].join(' ')}
              onClick={() => onPickDay?.(key)}
              data-trip-calendar-day={key}
            >
              <div className="flex items-center justify-between text-[11px]">
                <span className={isToday ? 'font-semibold text-blue-700 dark:text-blue-400' : 'text-muted-foreground'}>{d.getDate()}</span>
                {dayTrips.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-[10px] font-mono">
                    {dayTrips.length}
                  </span>
                )}
              </div>
              <div className="mt-1 space-y-0.5 overflow-hidden">
                {dayTrips.slice(0, 3).map((t) => (
                  <div
                    key={t.id}
                    className="text-[10px] font-mono truncate hover:underline cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); onPickTrip?.(t.id); }}
                    data-trip-calendar-trip={t.id}
                  >
                    {t.code}
                  </div>
                ))}
                {dayTrips.length > 3 && (
                  <div className="text-[10px] text-muted-foreground italic">+{dayTrips.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
